/**
 * CURVED-EARTH WORLD BEND (owner playtest 2026-08-01: "distance presentation").
 *
 * Every world-space vertex is pushed DOWN by
 *
 *     drop(d) = d² / (2R)
 *
 * where `d` is the HORIZONTAL (xz) distance from the camera and `R` is the
 * tuning radius below. This is the classic endless-runner world bend: near the
 * camera it does nothing (drop is 2 cm at 10 m), and far away it folds the
 * ground away from the eye so distant scenery SINKS below a horizon that is
 * much closer than the fog plane. It is pure presentation — the SIM is
 * completely untouched, contact/kick distances included — and it is orthogonal
 * to render/curve.ts, which bends the road LATERALLY. The two compose: curve.ts
 * moves objects in x, this module moves the resulting vertices in y.
 *
 * ---------------------------------------------------------------------------
 * WHY THE BEND, AND WHY THIS RADIUS
 * ---------------------------------------------------------------------------
 * With an eye height `h` the apparent angle BELOW the horizontal of a point at
 * horizontal distance d and local height y is
 *
 *     α(d, y) = atan( (h - y + d²/2R) / d )
 *
 * For y = 0 that has a MINIMUM at d* = sqrt(2Rh) of α_h = atan(sqrt(2h/R)).
 * α_h is the VISIBLE HORIZON: the ground surface never appears higher on screen
 * than that, and everything past d* projects BELOW the crest at d* and is
 * therefore hidden behind it by the depth test. So:
 *
 *   - `horizonDistance(h)` = d* = sqrt(2Rh) is how far the ROAD SURFACE is
 *     visible (the crest);
 *   - an object of height y is hidden while α(d, y) > α_h, i.e. until it rises
 *     over that crest at `appearDistance(y, h)`.
 *
 * The acceptance criterion from the brief is that a vehicle at the 240 m spawn
 * horizon (TUNING.traffic.spawnHorizonMin) is FULLY below the visible horizon,
 * so nothing ever pops into an empty frame. With the shipped camera
 * (h = TUNING.player.cameraHeight = 1.35 m) that is a hard upper bound on R —
 * see earthCurve.test.ts, which pins every number below:
 *
 *     R = 2200 m  ->  crest (road surface horizon)      77.1 m
 *                     car  (1.7 m) clears the crest at  163.6 m
 *                     semi (4.0 m) clears the crest at  209.7 m
 *                     semi at the 240 m spawn is hidden by 0.49°
 *                     car  at the 240 m spawn is hidden by 1.07°
 *
 * R must not go much above that: at R = 2900 a 4 m semi at 240 m is only just
 * hidden (0°) and at R = 4700 a 1.7 m car is only just hidden, so anything
 * larger fails the brief outright. Smaller R hides more but pulls the crest in
 * (R = 700, the brief's starting guess, puts it at 43 m — the road would end
 * two seconds ahead of the bike). 2200 is the value that hides the TALLEST
 * spawner vehicle with margin while leaving vehicles readable from ~164 m,
 * which is about where they emerge from the fog today (fogFar 200-210 m).
 *
 * Drops for reference: 0.02 m @ 10 m, 0.57 m @ 50 m, 2.27 m @ 100 m,
 * 9.09 m @ 200 m, 13.09 m @ 240 m.
 *
 * ---------------------------------------------------------------------------
 * HOW IT IS APPLIED
 * ---------------------------------------------------------------------------
 * `applyEarthCurve(material)` installs (or composes onto) the material's TSL
 * `positionNode`. three assigns `positionNode` into `positionLocal` AFTER
 * morph/skinning/instancing (NodeMaterial.setupPosition), so the node sees the
 * fully-posed local vertex and can safely round-trip through the model matrix:
 *
 *     world  = modelWorldMatrix * vec4(base, 1)
 *     drop   = |world.xz - origin.xz|² / 2R
 *     out    = base + (modelWorldMatrixInverse * vec4(0, -drop, 0, 0)).xyz
 *
 * Only a world-space Y OFFSET is added, so `positionWorld.xz` is unchanged and
 * anything keyed off it keeps working — in particular the asphalt detail node
 * (plastic.ts `asphaltMaterial`) still addresses road space by
 * `roadDistance - positionWorld.z` and `positionLocal.x`, which are both
 * untouched by a pure vertical offset through the road's yaw-only matrices.
 *
 * ORIGIN. The drop is measured from `setEarthCurveOrigin(x, z)` — a shared
 * uniform written once per frame with the render camera's world xz. It is
 * DELIBERATELY not TSL's `cameraPosition`: the shadow pass renders from the
 * sun's camera, and keying the bend off `cameraPosition` would bend the world
 * around the LIGHT in the depth pass and detach every shadow from its caster.
 * With a camera-independent uniform the colour pass and the shadow pass bend
 * identically, so shadows stay glued to their objects (three's renderer copies
 * a material's `positionNode` onto the shadow override material — see
 * Renderer._renderObjectDirect — so no extra wiring is needed).
 *
 * TESSELLATION. The drop is quadratic, so geometry is only as bent as it is
 * subdivided: a chord across a span L sits L²/(8R) BELOW the true surface.
 * That is 1.4 mm over the road's 5 m sub-segments (invisible) but 1.45 m across
 * a 160 m wheat stripe (which would vanish into the ground). Large flat pieces
 * therefore carry extra segments — see the call sites in biomes.ts. Because the
 * error is always DOWNWARD, a coarse ground plane can only sink further away
 * from the props standing on it, never poke through them.
 */

import { Vector2, type Material } from "three/webgpu";
import { modelWorldMatrix, positionLocal, uniform, vec3, vec4 } from "three/tsl";

/**
 * The bend radius (m). See the header for the derivation; changing it changes
 * BOTH how far the road is visible and whether a spawning vehicle is hidden, so
 * re-run earthCurve.test.ts after touching it.
 */
export const EARTH_CURVE_RADIUS = 2200;

/** Rider eye height (m) — TUNING.player.cameraHeight, duplicated pure-side. */
export const EYE_HEIGHT = 1.35;

/** Vertical drop (m) applied at horizontal distance `d` from the camera. */
export function earthDrop(d: number): number {
  return (d * d) / (2 * EARTH_CURVE_RADIUS);
}

/**
 * Horizontal distance (m) of the visible crest for an eye at height `h`: the
 * furthest point of the GROUND SURFACE that is not hidden behind nearer ground.
 */
export function horizonDistance(h: number = EYE_HEIGHT): number {
  return Math.sqrt(2 * EARTH_CURVE_RADIUS * h);
}

/** Angle (rad) below the horizontal at which the horizon crest appears. */
export function horizonAngle(h: number = EYE_HEIGHT): number {
  return Math.atan(Math.sqrt((2 * h) / EARTH_CURVE_RADIUS));
}

/** Apparent angle (rad) below the horizontal of a point at (d, local height y). */
export function angleBelowHorizon(d: number, y = 0, h: number = EYE_HEIGHT): number {
  return Math.atan((h - y + earthDrop(d)) / d);
}

/**
 * How far below the visible horizon a point of local height `y` sits at
 * distance `d`, in radians. Positive = HIDDEN behind the crest.
 */
export function hiddenMargin(d: number, y: number, h: number = EYE_HEIGHT): number {
  return angleBelowHorizon(d, y, h) - horizonAngle(h);
}

/**
 * Distance (m) at which the TOP of an object of height `y` first rises over the
 * crest — i.e. where it starts to appear. Beyond this it is fully hidden.
 */
export function appearDistance(y: number, h: number = EYE_HEIGHT): number {
  const R = EARTH_CURVE_RADIUS;
  const k = Math.sqrt((2 * h) / R); // tan of the horizon angle
  // d² - 2Rk·d + 2R(h - y) = 0, positive root.
  return R * k + Math.sqrt(R * R * k * k - 2 * R * (h - y));
}

// ---------------------------------------------------------------------------
// TSL side
// ---------------------------------------------------------------------------

/**
 * Loose stand-in for a TSL node. Same trick as plastic.ts: the real generic
 * builder types make `tsc` take minutes to unify a chain this long.
 */
interface TslNode {
  readonly [key: string]: unknown;
  sub(v: unknown): TslNode;
  mul(v: unknown): TslNode;
  add(v: unknown): TslNode;
  div(v: unknown): TslNode;
  dot(v: unknown): TslNode;
  element(i: number): TslNode;
}

/** Camera xz, written once per frame by `setEarthCurveOrigin`. */
const _origin = uniform(new Vector2(0, 0));
/** 1 / (2R) — a uniform so the radius can be retuned without a recompile. */
const _invTwoR = uniform(1 / (2 * EARTH_CURVE_RADIUS));

/**
 * Point the bend at the camera. Call ONCE per rendered frame, before
 * `renderer.render`, with the render camera's WORLD xz. Every curved material
 * shares this uniform, so one write moves the whole world's horizon.
 */
export function setEarthCurveOrigin(x: number, z: number): void {
  const v = (_origin as unknown as { value: Vector2 }).value;
  v.x = x;
  v.y = z;
}

/** Retune the radius live (debug/QA). Pass 0 or Infinity to flatten the world. */
export function setEarthCurveRadius(radius: number): void {
  (_invTwoR as unknown as { value: number }).value =
    radius > 0 && Number.isFinite(radius) ? 1 / (2 * radius) : 0;
}

/** Materials already curved — `applyEarthCurve` is idempotent per material. */
const _curved = new WeakSet<Material>();

/**
 * Install the world bend on a material, composing with any existing
 * `positionNode` (the existing node runs first and its LOCAL-space result is
 * what gets dropped). Idempotent, and safe on both classic and node materials:
 * three copies a classic material's own properties onto the node material it
 * builds for it.
 *
 * Apply to everything that lives in WORLD space — road, markings, rails, biome
 * props/backdrops/ground/water, tunnel, vehicles, wrecks. Do NOT apply to
 * camera-attached geometry (cockpit, kick legs) or to screen-space overlays,
 * and do not apply to the horizon haze band, which is deliberately a
 * camera-relative atmospheric element (biomes.ts).
 */
export function applyEarthCurve<T extends Material>(material: T): T {
  if (_curved.has(material)) return material;
  _curved.add(material);
  const host = material as Material & { positionNode?: unknown };
  const prev = host.positionNode as TslNode | null | undefined;
  const base = (prev && (prev as { isNode?: boolean }).isNode ? prev : positionLocal) as TslNode;

  const M: TslNode = modelWorldMatrix as unknown as TslNode;
  const origin: TslNode = _origin as unknown as TslNode;
  const homogeneous: TslNode = vec4(base as never, 1) as unknown as TslNode;
  const world: TslNode = M.mul(homogeneous);
  const dx: TslNode = (world.x as TslNode).sub(origin.x);
  const dz: TslNode = (world.z as TslNode).sub(origin.y);
  const d2: TslNode = dx.mul(dx).add(dz.mul(dz));
  const drop: TslNode = d2.mul(_invTwoR);

  // Back into the mesh's own space as a pure offset: only world Y moves, so
  // positionWorld.xz (and therefore every road-space shader coordinate) is
  // exactly what it was before the bend.
  //
  // The offset is M3⁻¹·(0, -drop, 0) — but taking it from `modelWorldMatrixInverse`
  // would cost a Matrix4 copy + invert on the CPU for EVERY object EVERY frame
  // (that uniform's onObjectUpdate), which measured as most of this feature's
  // submit-time cost across ~560 draws. Every transform in this project is a
  // TRS (translate · rotate · per-axis scale, no shear), so M3 = R·S with
  // orthonormal R, its columns are cᵢ = sᵢ·rᵢ, and
  //     (M3⁻¹·ŷ)ᵢ = rᵢ.y / sᵢ = cᵢ.y / |cᵢ|²
  // which is three cheap dot products in the vertex shader and zero CPU work.
  const axis = (i: number): TslNode => {
    const col: TslNode = M.element(i);
    const xyz: TslNode = col.xyz as TslNode;
    return (col.y as TslNode).div(xyz.dot(xyz));
  };
  const invY: TslNode = vec3(
    axis(0) as never,
    axis(1) as never,
    axis(2) as never,
  ) as unknown as TslNode;
  const offset: TslNode = invY.mul(drop);
  host.positionNode = base.sub(offset);
  return material;
}
